import React, { FC } from 'react'
import { Box, Flex } from '@/components/atoms/Grid'
import styled from 'styled-components'
import { useRouter } from 'next/router'
import { convertQueryParamsAndRemoveEmptyGetParameters } from '@/core/toStringHelper'
import { FilterQueryItem } from '@/types/filterQueryItem'
import { routerPushShallow } from '@/providers/filterProvider/filtersProvider'
import { validFor } from '@/theme/validFor'
import useUUID from 'hooks/useUUID'

type PagerProps = {
  numberOfPages: number
  currentPageIndex: number
  onClick: (page: number) => void
}

const Pager: FC<PagerProps> = ({
  numberOfPages,
  currentPageIndex,
  onClick
}) => {
  if (numberOfPages !== 0 && numberOfPages < 2) {
    return null
  }
  const router = useRouter()
  const allPages = Array.from({ length: Math.ceil(numberOfPages) })
  let previousValue: string
  const countPageBox = 3
  const pageKeys = useUUID(allPages.length)

  const pagesToDisplay: Array<{
    value: string
    isNumber: boolean
    index?: number
  }> = allPages
    .map((value, index) => {
      if (index < 0) {
        return null
      }
      if (
        index === 0 ||
        index + 1 === allPages.length ||
        (index + countPageBox > currentPageIndex && index < currentPageIndex) ||
        (currentPageIndex === 1 && index - countPageBox < 1) ||
        currentPageIndex === index ||
        index - 1 === currentPageIndex
      ) {
        previousValue = value?.toString()
        return { value: (index + 1).toString(), isNumber: true }
      }
      if (previousValue === '...') {
        return null
      }
      previousValue = '...'
      return { value: '...', isNumber: false }
    })
    .filter((dict) => dict)

  const pageClick = (page: string) => {
    if (page === (router.query.page as string)) {
      onClick(parseInt(page))
      return
    }
    router.query.page = page

    const stringQuery = convertQueryParamsAndRemoveEmptyGetParameters({
      query: router.query,
      allowOnly: [...Object.values(FilterQueryItem), 'page']
    })
    const basePath = router.asPath.split('?')[0]
    const path = `${basePath}${stringQuery ? '?' : ''}${stringQuery}`
    routerPushShallow(path, router, router.query)
  }
  return (
    <Wrapper>
      {pagesToDisplay.map(({ value, isNumber }, index) =>
        isNumber ? (
          <React.Fragment key={pageKeys[index]}>
            <PageBox
              isSelected={value === currentPageIndex.toString()}
              onClick={() => pageClick(value)}
            >
              {parseInt(value)}
            </PageBox>
          </React.Fragment>
        ) : (
          <MoreBox key={pageKeys[index] || value}>{value}</MoreBox>
        )
      )}
    </Wrapper>
  )
}

const PageBox = styled(Box)`
  border: 1px solid ${(props) => props.theme.colors.onBackground};
  border-radius: ${(props) => props.theme.buttonBorderRadius}px;

  font-size: 12px;
  color: ${(props) =>
    props.isSelected
      ? props.theme.colors.background
      : props.theme.colors.onBackground};
  background-color: ${(props) =>
    props.isSelected
      ? props.theme.colors.onBackground
      : props.theme.colors.background};
  padding: 8px 11px;
  ${validFor.mobile`
    padding: 8px 12px;
  `}

  ${validFor.desktop`
    &:hover {
        background-color: ${(props) => props.theme.colors.onBackground};
        color: ${(props) => props.theme.colors.background};
        cursor: pointer;
      }
  `}
`
const MoreBox = styled(Box)`
  padding: 2px 10px;
  ${validFor.mobile`
    padding: 2px 0;
  `}
`
const Wrapper = styled(Flex)`
  min-height: 32px;
  column-gap: 6px;
  margin-top: 0;
  margin-bottom: 0;
  justify-content: flex-end;

  ${validFor.mobile`
    column-gap: 11px;
    margin-top: 20px;
    margin-bottom: -2px;
    justify-content: center;
  `}
`

export default Pager
